import { Box, Typography, Link as MUILink } from "@mui/material";
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";

const baseStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 2,
  py: 3,
  width: "100%",
  borderBottom: "0.5px solid",
  borderColor: "divider",
  backgroundColor: "var(--main-color)",
  transition: "all 0.1s ease",
};

const sidebarLinkStyle = {
  ...baseStyle,
  cursor: "pointer",
  "&:hover": {
    borderLeft: "2px solid",
    borderColor: "#FFFFFF",
    backgroundColor: "var(--secondary-color)",
    borderTop: "0.5px solid #FFFFFF",
    borderBottom: "0.5px solid #FFFFFF",
  },
};

const sectionDividerStyle = {
  height: "1px",
  backgroundColor: "#FFFFFF",
  width: "100%",
};

const Sidebar = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [contractsOpen, setContractsOpen] = useState(true);
  const { user } = useAuth();

  const token = user?.access_token || user?.id_token;
  let accessRoles = [];

  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      accessRoles =
        decodedToken?.resource_access?.["realm-management"]?.roles || [];
    } catch (err) {
      console.error("Error decoding token:", err);
      accessRoles = [];
    }
  }

  const isAdmin =
    accessRoles.includes("view-realm") || accessRoles.includes("manage-users");

  const menuItems = [
    { type: "header", label: t("landing.home"), to: "/home" },
    {
      type: "link",
      label: t("dataOfferings.title"),
      to: "/dataoffering",
      indent: true,
    },
    {
      type: "link",
      label: t("dataModels.title"),
      to: "/datamodel",
      indent: true,
    },
    {
      type: "link",
      label: t("participant.participantDiscovery"),
      to: "/participantdiscovery",
      indent: true,
    },

    { type: "header", label: t("privateArea.title") },

    {
      type: "link",
      label: t("privateArea.activity"),
      to: "/actionlog",
      indent: true,
      parent: "home",
      section: "privatearea",
    },
    {
      type: "link",
      label: t("privateArea.datasets"),
      to: "/mydatasets",
      indent: true,
      parent: "home",
      section: "privatearea",
    },
    {
      type: "link",
      label: t("privateArea.mycontracts"),
      to: "/mycontracts",
      indent: true,
      parent: "home",
      section: "privatearea",
    },
    {
      type: "link",
      label: t("mypolicies"),
      to: "/policies",
      indent: true,
      parent: "home",
      section: "privatearea",
    },
    {
      type: "link",
      label: t("mydataservice"),
      to: "/dataservice",
      indent: true,
      parent: "home",
      section: "privatearea",
    },
    // {
    //   type: "toggle",
    //   label: t("privateArea.contracts"),
    //   toggleKey: "contracts",
    //   indent: true,
    // },
    {
      type: "link",
      label: t("privateArea.personalData"),
      to: "/profilepage",
      indent: true,
      section: "privatearea",
    },

    ...(isAdmin
      ? [
          { type: "header", label: t("admin.title") },
          {
            type: "link",
            label: t("admin.users"),
            to: "/usercontrol",
            indent: true,
          },
          {
            type: "link",
            label: t("admin.activity"),
            to: "/activitytracking",
            indent: true,
          },
          {
            type: "link",
            label: t("participant.participantManagement"),
            to: "/participantmanagement",
            indent: true,
          },
          // {
          //   type: "link",
          //   label: t("admin.backupRecovery"),
          //   to: "/backuprecovery",
          //   indent: true,
          // },
          // {
          //   type: "link",
          //   label: t("admin.notificationsAlerts"),
          //   to: "/notificationsalerts",
          //   indent: true,
          // },
          {
            type: "link",
            label: t("admin.register"),
            to: "/registerequests",
            indent: true,
          },
        ]
      : []),
  ];

  const getLinkProps = (item) => {
    if (item.type !== "link") return {};
    const state =
      item.section === "privatearea" ? { from: "privatearea" } : undefined;
    return {
      component: RouterLink,
      to: item.to,
      state,
      onClick: () => {
        if (typeof onClose === "function") onClose();
      },
      underline: "none",
      color: "inherit",
      role: "menuitem",
    };
  };

  return (
    open && (
      <Box
        onMouseLeave={onClose}
        sx={{
          position: "fixed",
          top: { xs: 56, sm: 64 },
          bottom: 0,
          left: 0,
          width: 320,
          backgroundColor: "var(--main-color)",
          display: "flex",
          flexDirection: "column",
          zIndex: 1200,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
        role="menu"
        aria-label="Main navigation"
      >
        {menuItems.map((item, index) => {
          if (item.type === "divider") {
            return <Box key={index} sx={sectionDividerStyle} />;
          }

          if (item.parent === "contracts" && !contractsOpen) {
            return null;
          }

          if (item.type === "toggle") {
            return (
              <Box
                key={index}
                sx={{
                  ...sidebarLinkStyle,
                  backgroundColor: "var(--main-color)",
                }}
                onClick={() => setContractsOpen((prev) => !prev)}
                role="button"
                aria-expanded={contractsOpen ? "true" : "false"}
                aria-controls="contracts-section"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  sx={{ pl: item.indent ? 4 : 2, pr: 2 }}
                >
                  <Typography
                    variant="body1"
                    color="#fff"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: "normal",
                      pl: item.indent ? 1 : 0,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    color="#fff"
                    sx={{
                      fontSize: "1rem",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    {contractsOpen ? "▾" : "▸"}
                  </Typography>
                </Box>
              </Box>
            );
          }

          const isSectionHeader =
            item.type === "header" &&
            (item.label === t("admin.title") ||
              item.label === t("privateArea.title"));

          if (isSectionHeader) {
            return (
              <Box
                key={index}
                sx={{
                  ...baseStyle,
                  backgroundColor: "var(--main-color)",
                  cursor: "default",
                  pl: item.indent ? 4 : 2,
                }}
              >
                <Typography
                  variant="body1"
                  color="#fff"
                  sx={{ fontSize: "0.85rem", fontWeight: "bold" }}
                >
                  {item.label}
                </Typography>
              </Box>
            );
          }

          // HOME como link especial
          if (item.type === "header" && item.label === t("landing.home")) {
            return (
              <MUILink
                key={index}
                {...getLinkProps({ type: "link", to: "/home" })}
                sx={{
                  ...sidebarLinkStyle,
                  pl: 2,
                  textDecoration: "none",
                  color: "#fff",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1rem", fontWeight: "normal" }}
                >
                  {item.label}
                </Typography>
              </MUILink>
            );
          }

          // Links normales
          if (item.type === "link") {
            return (
              <MUILink
                key={index}
                {...getLinkProps(item)}
                sx={{
                  ...sidebarLinkStyle,
                  pl: item.parent === "contracts" ? 7 : item.indent ? 4 : 2,
                  textDecoration: "none",
                  color: "#fff",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "normal",
                  }}
                >
                  {item.label}
                </Typography>
              </MUILink>
            );
          }

          return null;
        })}
      </Box>
    )
  );
};

export default Sidebar;
